import type { Granularidade } from '@/api/painel';
import { formatBRL } from '@/lib/format';

const num = (frac: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: frac, maximumFractionDigits: frac });
const n0 = num(0);
const n1 = num(1);
const n2 = num(2);

export const fmtInt = (v: number) => n0.format(v);
export const fmtKwh = (v: number) => `${(v >= 100 ? n0 : n2).format(v)} kWh`;
export const fmtKwhNum = (v: number) => (v >= 100 ? n0 : n1).format(v);
export const fmtPct = (v: number | null | undefined) => (v == null ? '—' : `${n1.format(v)}%`);
export const fmtBrl = (v: string | number) => formatBRL(v);

const SP = 'America/Sao_Paulo';
const dayFmt = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit' });
const monthFmt = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', month: 'short', year: '2-digit' });
const dtFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: SP,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit'
});
const dateFmt = new Intl.DateTimeFormat('pt-BR', { timeZone: SP, day: '2-digit', month: '2-digit', year: 'numeric' });

/** "2026-10-07" (data civil do backend, já em SP) → "07/10". */
export const fmtDay = (iso: string) => dayFmt.format(new Date(`${iso}T00:00:00Z`));
/** "2026-10-01" → "out. 26". */
export const fmtMonth = (iso: string) => monthFmt.format(new Date(`${iso}T00:00:00Z`)).replace(' de ', ' ');
/** "2026-10-01T14:00:00-03:00" (hora cheia, já em SP) → "01/10 14h". */
export const fmtHour = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 13)}h`;

export function labelFormatter(g: Granularidade): (iso: string) => string {
  return g === 'hora' ? fmtHour : g === 'mes' ? fmtMonth : fmtDay;
}

/** CO₂ evitado: até 1 t em kg, acima em toneladas. */
export function fmtCo2(kg: number): { value: string; unit: string } {
  return kg >= 1000 ? { value: n1.format(kg / 1000), unit: 't CO₂' } : { value: n0.format(kg), unit: 'kg CO₂' };
}

/** Variação % contra o período anterior; null quando não há base (sem acesso ou zero). */
export function deltaPct(atual: number, anterior: number | null | undefined): number | null {
  if (anterior == null || anterior <= 0) return null;
  return (100 * (atual - anterior)) / anterior;
}
/** Instante UTC → "07/10, 14:32" no horário de Brasília. */
export const fmtDateTime = (iso: string) => dtFmt.format(new Date(iso));
export const fmtDate = (iso: string) => dateFmt.format(new Date(iso));

export function fmtDuration(min: number | null): string {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export function fmtAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return 'sem contato registrado';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 90) return 'agora há pouco';
  const m = Math.round(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.round(h / 24)} dias`;
}
